/**
 * Seamless marquee (A + A) + horizontal drag scrubbing.
 * - Uses data-marquee-speed (px/sec, or <5 as multiplier of default).
 * - Leaves any existing data-speed alone (used by your vertical parallax).
 * - No external plugins required.
 */
(function () {
  const DEFAULT_SPEED = 60; // px/sec

  function imagesReady(root) {
    const imgs = root.querySelectorAll("img");
    if (!imgs.length) return Promise.resolve();
    let loaded = 0;
    return new Promise((res) => {
      imgs.forEach((img) => {
        if (img.complete) {
          if (++loaded === imgs.length) res();
        } else {
          img.addEventListener("load", () => {
            if (++loaded === imgs.length) res();
          });
          img.addEventListener("error", () => {
            if (++loaded === imgs.length) res();
          });
        }
      });
    });
  }

  function parseSpeed(root) {
    const raw = root.getAttribute("data-marquee-speed");
    const n = Number(raw);
    if (!isFinite(n) || n <= 0) return DEFAULT_SPEED;
    return n < 5 ? n * DEFAULT_SPEED : n; // 1.2 => 72 px/s
  }

  function measureSeedWidth(track, seedCount) {
    if (!seedCount) return track.scrollWidth;
    const first = track.children[0];
    const last = track.children[seedCount - 1];
    if (!first || !last) return track.scrollWidth;
    const parentLeft = track.getBoundingClientRect().left;
    const left = first.getBoundingClientRect().left - parentLeft;
    const right = last.getBoundingClientRect().right - parentLeft;
    return Math.round(right - left);
  }

  function initMarquee(root) {
    if (!window.gsap) {
      console.error("[marquee] GSAP not found.");
      return;
    }
    const hasST = !!(
      window.ScrollTrigger || gsap.core?.globals?.().ScrollTrigger
    );
    if (hasST) gsap.registerPlugin(ScrollTrigger);

    const track = root.querySelector(".marquee__track");
    if (!track) {
      console.warn("[marquee] Missing .marquee__track");
      return;
    }

    const pxPerSec = parseSpeed(root);
    const quickSetX = gsap.quickSetter(track, "x", "px");

    // snapshot seed
    const seed = Array.from(track.children);
    if (!seed.length) {
      console.warn("[marquee] No items");
      return;
    }

    let seedWidth = 0;
    let duration = 0;
    let wrapX; // gsap wrap fn for [-seedWidth, 0]
    let tween; // base loop tween
    let dragging = false; // drag state
    let posX = 0; // current x (kept in sync with GSAP transform)
    let startX = 0; // pointerdown screen X
    let startPos = 0; // x at pointerdown (posX snapshot)
    let lastMoveX = 0;
    let lastMoveT = 0;
    let vx = 0; // px/s during drag (for inertia)

    function setX(x) {
      // store wrapped X and apply quickly
      posX = wrapX(parseFloat(x));
      quickSetX(posX);
    }

    function buildAA() {
      // reset to just seed
      while (track.children.length > seed.length)
        track.removeChild(track.lastElementChild);
      // normalize seed order in DOM
      if (track.children.length !== seed.length) {
        track.innerHTML = "";
        seed.forEach((n) => track.appendChild(n.cloneNode(true)));
      }
      // append duplicate (A + A)
      seed.forEach((n) => track.appendChild(n.cloneNode(true)));
    }

    function buildTween() {
      seedWidth = measureSeedWidth(track, seed.length);
      if (!seedWidth) {
        console.warn("[marquee] seedWidth=0");
        return null;
      }

      wrapX = gsap.utils.wrap(-seedWidth, 0);
      duration = seedWidth / pxPerSec;

      gsap.set(track, { x: 0, force3D: true });
      posX = 0;

      const t = gsap.to(track, {
        x: -seedWidth,
        ease: "none",
        duration,
        repeat: -1,
        modifiers: {
          x: (x) => wrapX(parseFloat(x)) + "px",
        },
        onUpdate() {
          // keep posX in sync while tween runs
          posX = gsap.getProperty(track, "x");
        },
      });

      if (hasST) {
        const MAX = 3,
          FACTOR = 0.003;
        ScrollTrigger.create({
          trigger: root,
          start: "top bottom",
          end: "bottom top",
          onUpdate(self) {
            if (dragging) return; // ignore scroll influence while dragging
            const v = self.getVelocity();
            const boost = gsap.utils.clamp(-MAX, MAX, v * FACTOR);
            let ts = 1 + boost;
            if (Math.abs(ts) < 0.1) ts = 0.1 * Math.sign(ts || 1);
            t.timeScale(ts);
          },
        });
      }

      // reduced motion
      const mq = matchMedia("(prefers-reduced-motion: reduce)");
      const apply = (e) => t.paused(e.matches);
      apply(mq);
      mq.addEventListener?.("change", apply);

      return t;
    }

    function tweenTimeFromPos(x) {
      // distance moved left is positive: dist = wrap(0..seedWidth, -x)
      const dist = ((-x % seedWidth) + seedWidth) % seedWidth; // 0..seedWidth
      return dist / pxPerSec; // seconds
    }

    function rebuild() {
      if (tween) tween.kill();
      gsap.set(track, { clearProps: "transform" });
      buildAA();
      // wait one frame for layout before measuring
      requestAnimationFrame(() => {
        tween = buildTween();
        root._marqueeTween = tween;
      });
    }

    // ---- Dragging (pointer events)
    function onPointerDown(e) {
      const isPrimary = e.isPrimary ?? true;
      if (!isPrimary) return;
      dragging = true;
      root.classList.add("is-dragging");
      startX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0;
      startPos = posX;
      lastMoveX = startX;
      lastMoveT = performance.now();
      vx = 0;

      if (tween) tween.pause();

      // prevent text selection while dragging with mouse
      document.addEventListener("selectstart", preventDefaultOnce, {
        passive: false,
      });
      window.addEventListener("pointermove", onPointerMove, { passive: false });
      window.addEventListener("pointerup", onPointerUp, { passive: true });
      window.addEventListener("pointercancel", onPointerUp, { passive: true });
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const x = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0;
      const dx = x - startX;

      // apply delta
      setX(startPos + dx);

      // velocity (px/s)
      const now = performance.now();
      const dt = Math.max(16, now - lastMoveT); // ms
      vx = (x - lastMoveX) / (dt / 1000); // px/s, positive = drag right

      lastMoveX = x;
      lastMoveT = now;

      // if horizontal move is happening, prevent default to avoid accidental text selection/drag
      if (Math.abs(dx) > 2) e.preventDefault?.();
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      root.classList.remove("is-dragging");

      // sync tween's internal time with current posX so it resumes seamlessly
      if (tween) {
        const time = tweenTimeFromPos(posX) % duration;
        tween.time(time);
      }

      // simple inertia: convert drag velocity into a brief timeScale boost
      if (tween) {
        // Drag right (vx > 0) should push marquee to move right => negative timescale
        // Base loop moves left (negative x), so sign is inverted.
        const MAX = 3,
          FACTOR = 0.004; // tune to feel
        let ts = 1 - vx * FACTOR; // vx>0 => ts<1 (reverse if strong)
        ts = gsap.utils.clamp(-MAX, MAX, ts);
        if (Math.abs(ts) < 0.1) ts = 0.1 * Math.sign(ts || 1);
        tween.play().timeScale(ts);

        // ease timescale back to 1 smoothly
        gsap.to(tween, { timeScale: 1, duration: 0.8, ease: "power2.out" });
      }

      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      document.removeEventListener("selectstart", preventDefaultOnce);
    }

    function preventDefaultOnce(e) {
      e.preventDefault();
    }

    // boot
    rebuild();

    // listeners
    track.addEventListener("pointerdown", onPointerDown, { passive: true });

    // Rebuild on resize
    let rTO;
    addEventListener(
      "resize",
      () => {
        clearTimeout(rTO);
        rTO = setTimeout(rebuild, 150);
      },
      { passive: true }
    );
  }

  async function boot() {
    const all = document.querySelectorAll(".marquee");
    if (!all.length) return;
    await Promise.all(Array.from(all).map(imagesReady));
    all.forEach(initMarquee);
  }

  if (document.readyState === "complete") boot();
  else addEventListener("load", boot, { once: true });
})();
